import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { 
  createAccessibleFormFieldAttributes,
  createKeyboardNavigationHandler
} from '../utils/consolidatedAccessibility.js';
import { getTouchFriendlyClasses } from '../utils/responsiveUtils.js';
import { getFocusClasses } from '../utils/consolidatedAccessibility.js';

const TimeRangeSelector = ({ 
  value, 
  onChange, 
  options, 
  loading = false, 
  error = null,
  description = null,
  className = '',
  'aria-label': ariaLabel = 'Select time period for analytics data'
}) => {
  const { theme } = useTheme();
  const selectorClasses = {
    container: 'relative',
    label: 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2',
    select: 'block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500',
    error: 'mt-1 text-sm text-red-600 dark:text-red-400'
  };
  const touchClasses = getTouchFriendlyClasses();
  const focusClasses = getFocusClasses(theme, error ? 'error' : 'primary');
  
  const fieldId = 'time-range-selector';
  const fieldAttributes = createAccessibleFormFieldAttributes(
    fieldId,
    ariaLabel,
    error,
    description,
    false
  );
  
  const handleKeyDown = createKeyboardNavigationHandler(
    (e) => {
      // Handle keyboard navigation for select
      if (e.key === 'Enter' || e.key === ' ') {
        e.target.click();
      }
    }
  );

  return (
    <div className={`${selectorClasses.container} ${className}`}>
      <label 
        htmlFor={fieldId} 
        className={selectorClasses.label}
      >
        <span className="flex items-center">
          <span className="mr-2" aria-hidden="true">📅</span>
          Time Period
        </span>
      </label>
      
      {description && (
        <p 
          id={`${fieldId}-description`}
          className="text-sm text-gray-500 dark:text-gray-400 mb-2"
        >
          {description}
        </p>
      )}
      
      <div className="relative">
        <select
          {...fieldAttributes}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          className={`
            ${selectorClasses.select}
            ${touchClasses.select}
            ${focusClasses}
            ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            ${error ? 'border-red-300 dark:border-red-600' : ''}
          `}
        >
          {options.map((option) => (
            <option 
              key={option.value} 
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        
        {/* Loading indicator */}
        {loading && (
          <div 
            className="absolute right-3 top-1/2 transform -translate-y-1/2"
            aria-hidden="true"
          >
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-500" />
          </div>
        )}
        
        {/* Custom dropdown arrow for better visibility */}
        {!loading && (
          <div 
            className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
            aria-hidden="true"
          >
            <svg 
              className="w-4 h-4 text-gray-400 dark:text-gray-500" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        )}
      </div>
      
      {error && (
        <p 
          id={`${fieldId}-error`}
          className="mt-1 text-sm text-red-600 dark:text-red-400"
          role="alert"
          aria-live="polite"
        >
          {error}
        </p>
      )}
      
      {/* Screen reader only status */}
      <div className="sr-only" aria-live="polite">
        {loading ? 'Loading time period options...' : 
         `Time period selector. Current selection: ${options.find(opt => opt.value === value)?.label || value}`}
      </div>
    </div>
  );
};

export default TimeRangeSelector;
